import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DESIGN_REQUESTS } from "@/constants/designRequestTestIds";

const STATUS_COPY = {
  draft:     { label: "Draft",            bg: "#6B6358", chip: "Ready to submit" },
  submitted: { label: "Pending approval", bg: "#C28A2E", chip: "Awaiting approver" },
  approved:  { label: "Approved",         bg: "#1F6B6B", chip: "Cleared for export" },
  rejected:  { label: "Rejected",         bg: "#B5462F", chip: "Returned with notes" },
};

export default function ApprovalBanner({ document, errorCount, onChange }) {
  const { user } = useAuth();
  const status = document?.approval_status || "draft";
  const meta = STATUS_COPY[status] || STATUS_COPY.draft;
  const [action, setAction] = useState(null); // 'submit' | 'approve' | 'reject' | 'withdraw'
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const isAdmin = user?.is_platform_admin;
  const canSubmit = status === "draft" || status === "rejected";
  const canDecide = status === "submitted" && isAdmin;
  const canWithdraw = status === "submitted" && (isAdmin || document?.submitted_by === user?.user_id);

  const close = () => { setAction(null); setComment(""); setErr(""); };

  const post = async (verb) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(
        `/document-versions/${document.document_version_id}/${verb}`,
        { comment: comment.trim() || null }
      );
      close();
      onChange?.(data);
    } catch (e) {
      setErr(e.response?.data?.detail || `Could not ${verb}`);
    } finally {
      setBusy(false);
    }
  };

  const exportNow = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(
        `/document-versions/${document.document_version_id}/export`,
        {}
      );
      onChange?.(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const history = document?.approval_history || [];

  return (
    <div
      data-testid={DESIGN_REQUESTS.approvalBanner}
      className="mt-5 border rounded-lg overflow-hidden"
      style={{ borderColor: `${meta.bg}40`, backgroundColor: `${meta.bg}0a` }}
    >
      <div className="px-5 py-4 flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            data-testid={DESIGN_REQUESTS.approvalStatus}
            className="px-3 py-1 rounded-full text-xs uppercase tracking-[0.18em] font-medium"
            style={{ backgroundColor: meta.bg, color: "#F8F5EE" }}
          >
            {meta.label}
          </span>
          <span className="text-sm text-[#3D3A35]">{meta.chip}</span>
          {errorCount > 0 && status === "draft" && (
            <span className="text-xs text-[#B5462F]">
              {errorCount} blocker{errorCount === 1 ? "" : "s"} must be resolved before submitting.
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {canSubmit && (
            <button
              data-testid={DESIGN_REQUESTS.submitButton}
              disabled={errorCount > 0}
              onClick={() => setAction("submit")}
              className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium disabled:opacity-40"
              title={errorCount > 0 ? "Resolve blockers first" : "Submit for approval"}
            >
              Submit for approval
            </button>
          )}
          {canDecide && (
            <>
              <button
                data-testid={DESIGN_REQUESTS.approveButton}
                onClick={() => setAction("approve")}
                className="px-4 py-2 bg-[#1F6B6B] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium"
              >
                Approve
              </button>
              <button
                data-testid={DESIGN_REQUESTS.rejectButton}
                onClick={() => setAction("reject")}
                className="px-4 py-2 border border-[#B5462F] text-[#B5462F] rounded-full text-xs uppercase tracking-[0.18em] font-medium"
              >
                Reject
              </button>
            </>
          )}
          {canWithdraw && (
            <button
              data-testid={DESIGN_REQUESTS.withdrawButton}
              onClick={() => setAction("withdraw")}
              className="px-4 py-2 border border-[#6B6358] text-[#3D3A35] rounded-full text-xs uppercase tracking-[0.18em]"
            >
              Withdraw
            </button>
          )}
        </div>
      </div>

      {action && (
        <div className="px-5 py-4 bg-[#F8F5EE] border-t border-[#1A1F2C]/10">
          <p className="text-xs uppercase tracking-[0.18em] text-[#6B6358] mb-2">
            {action === "submit" && "Confirm submit"}
            {action === "approve" && "Confirm approval"}
            {action === "reject" && "Reject with reason"}
            {action === "withdraw" && "Withdraw submission"}
          </p>
          <textarea
            data-testid={DESIGN_REQUESTS.approvalCommentInput}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={action === "reject" ? "Required — explain what to fix." : "Optional comment for the audit trail."}
            rows={2}
            className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md text-sm bg-white"
          />
          {err && <p className="text-xs text-[#B5462F] mt-2">{err}</p>}
          <div className="mt-3 flex gap-2">
            <button
              data-testid={DESIGN_REQUESTS.approvalConfirmYes}
              disabled={busy || (action === "reject" && !comment.trim())}
              onClick={() => post(action)}
              className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium disabled:opacity-50"
            >
              {busy ? "Working…" : "Confirm"}
            </button>
            <button
              data-testid={DESIGN_REQUESTS.approvalConfirmCancel}
              onClick={close}
              className="px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#6B6358]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <details className="px-5 py-3 border-t border-[#1A1F2C]/10 bg-white/40">
          <summary className="text-xs cursor-pointer text-[#3D3A35]">
            Audit trail ({history.length})
          </summary>
          <ul className="mt-2 space-y-2 text-xs text-[#3D3A35]">
            {history.slice().reverse().map((h, i) => (
              <li
                key={i}
                data-testid={`${DESIGN_REQUESTS.approvalHistoryItemPrefix}${i}`}
                className="border-l-2 border-[#1A1F2C]/20 pl-3"
              >
                <div>
                  <strong>{h.event}</strong> by {h.actor_name || h.actor_email || h.actor_id} ·{" "}
                  <span className="text-[#6B6358]">{new Date(h.at).toLocaleString()}</span>
                </div>
                {h.comment && <div className="text-[#6B6358] italic mt-0.5">"{h.comment}"</div>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {status === "approved" && (
        <div className="px-5 py-4 border-t border-[#1A1F2C]/10 bg-[#1F6B6B]/5 flex flex-wrap gap-3 items-center justify-between">
          <div className="text-sm text-[#3D3A35] max-w-xl">
            <strong>Production handoff:</strong> approved documents can be rendered through
            Pica9 PDFMaker. The returned URL is the production-ready CMYK PDF and the
            fulfillment payload bundles it with brand + location context.
          </div>
          <div className="flex gap-2 items-center">
            {document.exported_pdf_url ? (
              <>
                <a
                  data-testid={DESIGN_REQUESTS.exportLink}
                  href={document.exported_pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium"
                >
                  Open PDF
                </a>
                <a
                  data-testid={DESIGN_REQUESTS.fulfillmentLink}
                  href={`${api.defaults.baseURL}/document-versions/${document.document_version_id}/fulfillment`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 border border-[#1F6B6B] text-[#1F6B6B] rounded-full text-xs uppercase tracking-[0.18em] font-medium"
                >
                  Fulfillment JSON
                </a>
                <button
                  data-testid={DESIGN_REQUESTS.exportButton}
                  disabled={busy}
                  onClick={exportNow}
                  className="text-xs uppercase tracking-[0.18em] text-[#6B6358] px-2"
                >
                  Re-export
                </button>
              </>
            ) : (
              <button
                data-testid={DESIGN_REQUESTS.exportButton}
                disabled={busy}
                onClick={exportNow}
                className="px-4 py-2 bg-[#1F6B6B] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium disabled:opacity-50"
              >
                {busy ? "Sending to PDFMaker…" : "Export to PDFMaker"}
              </button>
            )}
          </div>
          {err && <p className="text-xs text-[#B5462F] w-full">{err}</p>}
        </div>
      )}
    </div>
  );
}
